import React, { useMemo, useRef, useState } from 'react';
import {
	Box,
	Text,
	AlertDialog,
	AlertDialogBody,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogContent,
	AlertDialogOverlay,
	Button,
	Spacer,
} from 'src/components/ui';
import DDImage from './DDImage';
import CardActions from '../creatives/CardActions';
import { IDesignDirection } from 'src/lib/schemas/campaign/newFlowCampaign';

interface PromoCardProps {
	designDirection: IDesignDirection;
	openEditModal: () => void;
	onRetry: (designDirection: IDesignDirection) => void;
	onSelect: (designDirection: IDesignDirection) => void;
	onRemove: (id: string) => void;
	isSelected: boolean;
	isAnySelected: boolean;
}

const DDCard: React.FC<PromoCardProps> = ({
	designDirection,
	openEditModal,
	onRetry,
	onSelect,
	onRemove,
	isSelected,
	isAnySelected,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const cancelRef = useRef(null);
	const { status } = designDirection;
	const isLoading = useMemo(() => status !== 'GENERATED', [status]);
	const hasError = useMemo(
		() => status === 'ERROR' || status === 'failed',
		[status],
	);
	// Only disable Edit action when loading, but allow Remove even during loading
	const disabledActions = isLoading || hasError;
	const onClose = () => setIsOpen(false);
	const [isHovering, setIsHovering] = useState(false);

	const handleRemoveClick = () => {
		console.log('🗑️ [DDCard] Remove button clicked:', {
			designDirectionId: designDirection.id,
			variant: designDirection.variant,
			status: designDirection.status,
			isLoading,
			isSelected,
		});
		setIsOpen(true);
	};

	const handleConfirmRemove = () => {
		console.log('✅ [DDCard] Confirmed removal, calling onRemove:', {
			designDirectionId: designDirection.id,
			variant: designDirection.variant,
		});
		onRemove(designDirection.id);
		setIsOpen(false);
	};

	const outerBoxStyles = {
		opacity: 1,
		// Don't show 'not-allowed' cursor since Remove is always available
		// Only the image area should indicate it's not selectable during loading
		cursor: 'default',
	};

	const handleSelectChange = (selected: boolean) => {
		if (!isLoading) {
			onSelect(designDirection);
		}
	};

	const handleCardClick = () => {
		if (!isLoading) {
			onSelect(designDirection);
		}
	};

	return (
		<Box
			w={{ base: '100%', sm: '323px' }}
			maxW={{ base: 'calc(100vw - 32px)', sm: '323px' }}
			{...outerBoxStyles}
			borderRadius="16px"
			boxShadow={isSelected ? '0 0 0 2px #F75A23' : 'md'}
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
			p={isSelected ? '1px' : '0'}
			bg={isSelected ? 'white' : 'transparent'}
		>
			<Box
				borderTopRadius="16px"
				overflow="hidden"
				onClick={handleCardClick}
				position="relative"
				height="323px"
			>
				<DDImage
					designDirection={designDirection}
					onRetry={onRetry}
					onSelect={onSelect}
					isSelected={isSelected}
				/>
			</Box>
			<CardActions
				title={`Option ${designDirection.variant}`}
				onEdit={
					designDirection.adEngine === 'creatomate' ? openEditModal : undefined
				}
				onRemove={handleRemoveClick}
				isSelected={isSelected}
				disabledActions={disabledActions}
				onSelectChange={handleSelectChange}
				showCheckbox={true}
				borderRadiusValues="0 0 16px 16px"
			/>

			<AlertDialog
				isOpen={isOpen}
				leastDestructiveRef={cancelRef}
				onClose={onClose}
				isCentered
			>
				<AlertDialogOverlay zIndex={1900}>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Delete Option {designDirection.variant}
						</AlertDialogHeader>

						<AlertDialogBody>
							<Text>
								Are you sure you want to delete this design direction?
							</Text>
						</AlertDialogBody>

						<AlertDialogFooter>
							<Button ref={cancelRef} onClick={onClose}>
								Cancel
							</Button>
							<Button colorScheme="blue" onClick={handleConfirmRemove} ml={3}>
								Delete
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Box>
	);
};

export default DDCard;
